/**
 * LinkPopover — input inline para crear/editar/quitar un link.
 *
 * Se monta dentro del BubbleToolbar cuando el user clickea el botón
 * "link". Reemplaza visualmente los botones del toolbar mientras está
 * abierto. Confirma con Enter, cancela con Escape, quita link con
 * el botón de basura.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Trash2, X } from 'lucide-react'
import type { Editor } from '@tiptap/react'

interface LinkPopoverProps {
  editor: Editor
  onClose: () => void
}

export function LinkPopover({ editor, onClose }: LinkPopoverProps) {
  const { t } = useTranslation('editor')
  const inputRef = useRef<HTMLInputElement>(null)
  const initial = editor.getAttributes('link')?.['href'] ?? ''
  const [href, setHref] = useState<string>(typeof initial === 'string' ? initial : '')

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  function commit(): void {
    const trimmed = href.trim()
    if (!trimmed) {
      removeLink()
      return
    }
    // TipTap valida internamente; añadimos prefijo si falta.
    const normalized =
      /^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run()
    onClose()
  }

  function removeLink(): void {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    onClose()
  }

  return (
    <div className="flex items-center gap-1 px-2 py-1.5">
      <input
        ref={inputRef}
        type="url"
        value={href}
        onChange={(e) => setHref(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
          }
        }}
        placeholder={t('link.placeholder')}
        className="bg-canvas border border-line rounded-sm px-2 py-1 text-xs text-ink w-56 focus:outline-none focus:border-brand"
      />
      <button
        type="button"
        onClick={commit}
        title={t('link.confirm')}
        className="p-1 rounded-sm text-ink-muted hover:text-ink hover:bg-surface-muted"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      {initial ? (
        <button
          type="button"
          onClick={removeLink}
          title={t('link.remove')}
          className="p-1 rounded-sm text-ink-muted hover:text-critical hover:bg-critical-soft"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={onClose}
        title={t('link.cancel')}
        className="p-1 rounded-sm text-ink-muted hover:text-ink hover:bg-surface-muted"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
